import { useHistory, useLocation } from "react-router";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { ref, onValue } from "firebase/database";
import pjson from '../../../package.json';

// @material-ui/core components
import InputAdornment from "@material-ui/core/InputAdornment";
import { makeStyles } from "@material-ui/core/styles";
import { Box } from "@material-ui/core";
import DialpadIcon from '@material-ui/icons/Dialpad'
import AddAlert from "@material-ui/icons/AddAlert";
import Email from "@material-ui/icons/Email";
import Icon from "@material-ui/core/Icon";

// core components
import CustomInput from "components/CustomInput/CustomInput.js";
import GridContainer from "components/Grid/GridContainer.js";
import VersionSelector from "components/VersionSelector";
import Button from "components/CustomButtons/Button.js";
import CardHeader from "components/Card/CardHeader.js";
import Snackbar from "components/Snackbar/Snackbar.js";
import CardFooter from "components/Card/CardFooter.js";
import GridItem from "components/Grid/GridItem.js";
import CardBody from "components/Card/CardBody.js";
import Loader from "components/Loader/Loader.js";
import Card from "components/Card/Card.js";

import styles from "assets/jss/material-dashboard-pro-react/views/loginPageStyle.js";
import loginLogo from "assets/img/login-logo.png";
import logo from "assets/img/logo.png";

import { FETCH_LOGIN_REQUEST, RESET_PASSWORD_REQUEST, RESET_PASSWORD_UPDATE_REQUEST, BACK_TO_OTP_REQUEST} from "../../redux/users/actions";
import { DEFAULT_API_ENDPOINT, updateApiEndpoint, API_ENDPOINTS, subscribeToEndpointUpdates } from "../../common/constants";
import { useComposeDispatcher } from "../../common/hooks";
import { firebaseDB, setupDB } from "../../firebase";
import { hasQueryParam } from "common/helpers";

const useStyles = makeStyles(styles);

export default function LoginPage() {
  const history = useHistory();
  const location = useLocation();
  const _selectedVersion = localStorage.getItem('selected-api-version') || DEFAULT_API_ENDPOINT;

  const [selectedVersion, setSelectedVersion] = useState(_selectedVersion);
  const [currentEndpoints, setCurrentEndpoints] = useState(API_ENDPOINTS);
  const [showVersionSelector, setShowVersionSelector] = useState(false);
  const [firebaseInitialized, setFirebaseInitialized] = useState(false);
  const [cardAnimaton, setCardAnimation] = useState("cardHidden");
  const [showPassword, setShowPassword] = useState(false);
  const [loginSection, setLoginSection] = useState(true);
  const [otpSection, setOtpSection] = useState(false);
  const [loginInfo, setLoginInfo] = useState({});
  const [trMsg, setTRMsg] = useState('');
  const [tr, setTR] = useState(false);
  
  const userData = useSelector(({ users }) => users);

  const [resetPasswordUpdate] = useComposeDispatcher(RESET_PASSWORD_UPDATE_REQUEST);
  const [resetPassword] = useComposeDispatcher(RESET_PASSWORD_REQUEST);
  const [backToOTP] = useComposeDispatcher(BACK_TO_OTP_REQUEST);
  const [login] = useComposeDispatcher(FETCH_LOGIN_REQUEST);

  const handleVersionChange = (newVersion) => {
    setSelectedVersion(newVersion);
    localStorage.setItem('selected-api-version', newVersion);
    const { updateApiBaseUrl } = require("../../common/constants");
    updateApiBaseUrl();
    window.location.reload();
  };

  useEffect(() => {
    if (!firebaseInitialized) {
      
      try {
        setupDB();
        
        setTimeout(() => {
          if (!firebaseDB) {
            console.error('firebaseDB is still not available after setup');
            return;
          }
          
          const endpointsRef = ref(firebaseDB, "endpoints_config");
          const unsubscribeDBRef = onValue(endpointsRef, (snapshot) => {
            const fetchedData = snapshot.val();
            if (fetchedData) {
              console.log(fetchedData, 'fetchedData');
              for(const key in fetchedData) {
                updateApiEndpoint(key, fetchedData[key]);
              }
            } else {
              console.log('No data received from Firebase or data is null/undefined');
            }
          });

          return () => {
            unsubscribeDBRef();
          };
        }, 1000); // Wait 1 second for Firebase to initialize
        
        setFirebaseInitialized(true);
      } catch (error) {
        console.error('Error in Firebase setup:', error);
      }
    } else {
      console.log('Firebase already initialized, skipping setup');
    }
  }, [firebaseInitialized]);

  // Handle Enter key press for login
  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && loginSection) {
      if (loginInfo && loginInfo.emailAddress && loginInfo.password) {
        const _loginInfo = {
          ...loginInfo,
          emailAddress: loginInfo.emailAddress.trim()
        };
        login(_loginInfo)
      } else {
        setTR(true);
        setTRMsg("All fields are mandatory.");
      }
    }
  };

  useEffect(() => {
    if (userData.user?.userId && localStorage.getItem("user-details")) {
      history.push("/admin/dashboard");
    }

    let id = setTimeout(function () {
      setCardAnimation("");
    }, 700);
    // Specify how to clean up after this effect:
    return function cleanup() {
      window.clearTimeout(id);
    };
  });

  useEffect(() => {
    const showBanner = hasQueryParam('showbanner');
    setShowVersionSelector(showBanner);
  }, [location.search]);

  // Subscribe to endpoint updates
  useEffect(() => {
    const unsubscribe = subscribeToEndpointUpdates((updatedEndpoints) => {
      setCurrentEndpoints({ ...updatedEndpoints });
    });
    return unsubscribe;
  }, []);

  const validateResetPasswordUpdate = (data) => {
    const passwordReq = /^(?=.*\d)(?=.*[!@#$%^&*])(?=.*[a-z])(?=.*[A-Z]).{8,}$/;
    if (
      !data.emailAddress ||
      !data.confirmPassword ||
      !data.newPassword ||
      !data.otpCode
    ) {
      setTR(true);
      setTRMsg("All fields are mandatory.");
    } else {
      if (!passwordReq.test(data?.newPassword)) {
        setTRMsg("Please enter valid password");
        setTR(true);
      } else if (data?.confirmPassword !== data.newPassword) {
        setTRMsg("Entered Password is not matching");
        setTR(true);
      } else {
        resetPasswordUpdate(data);
      }
    }
  }

  if(userData.sentResetPassword && !otpSection) {
    setOtpSection(true);
  }

  if(userData.resetPasswordStatus && otpSection) {
    setOtpSection(false);
    setLoginInfo({});
    setLoginSection(true);
  }

  const classes = useStyles();
  return (
    <div className={classes.container}>
      <GridContainer justifyContent="center">
        <GridItem xs={12} sm={6} md={4}>
          <form onKeyPress={handleKeyPress}>
            <Card login className={classes[cardAnimaton]}>
              <CardHeader
                className={`${classes.cardHeader} ${classes.textCenter}`}
                color="danger"
              >
                <img
                  alt="logo"
                  src={loginLogo}
                  style={{ width: "120px", borderRadius: "5px" }}
                />
                {/* <h4 className={classes.cardTitle}>Log in</h4> */}
              </CardHeader>
              {showVersionSelector && (
                <VersionSelector 
                  selectedVersion={selectedVersion}
                  onVersionChange={handleVersionChange}
                />
              )}
              {!showVersionSelector && <Box
                style={{
                  padding: "8px 0",
                  textAlign: "center",
                  fontSize: "11px",
                  color: "#666666",
                  textTransform: "capitalize",
                  fontStyle: "italic",
                  borderTop: "1px solid #f0f0f0",
                  paddingTop: "8px",
                }}
              >
                {selectedVersion?.split("_").join(" ")}
              </Box>}
              
              <CardBody>
                {
                  otpSection &&
                  <>
                    <CustomInput
                      labelText="OTP Code"
                      id="otpCode"
                      formControlProps={{
                        fullWidth: true,
                      }}
                      data={loginInfo}
                      onChange={setLoginInfo}
                      inputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <DialpadIcon className={classes.inputAdornmentIcon} />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <CustomInput
                      labelText="New Password"
                      id="newPassword"
                      formControlProps={{
                        fullWidth: true,
                      }}
                      data={loginInfo}
                      onChange={setLoginInfo}
                      inputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Icon className={classes.inputAdornmentIcon}>
                              lock_outline
                          </Icon>
                          </InputAdornment>
                        ),
                        type: "password",
                        autoComplete: "off",
                      }}
                    />
                    <CustomInput
                      labelText="Confirm Password"
                      id="confirmPassword"
                      formControlProps={{
                        fullWidth: true,
                      }}
                      data={loginInfo}
                      helperText="Note: Please enter password which contain min 8 letter, at least a symbol, upper and lower case letters and a number"
                      onChange={setLoginInfo}
                      inputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Icon className={classes.inputAdornmentIcon}>
                              lock_outline
                          </Icon>
                          </InputAdornment>
                        ),
                        type: "password",
                        autoComplete: "off",
                      }}
                    />
                  </>
                }
                {
                  !otpSection &&
                  <CustomInput
                    labelText="Email"
                    id="emailAddress"
                    formControlProps={{
                      fullWidth: true,
                    }}
                    data={loginInfo}
                    onChange={setLoginInfo}
                    inputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Email className={classes.inputAdornmentIcon} />
                        </InputAdornment>
                      ),
                    }}
                  />
                }
                {loginSection && (
                  <CustomInput
                    labelText="Password"
                    id="password"
                    formControlProps={{
                      fullWidth: true,
                    }}
                    data={loginInfo}
                    onChange={setLoginInfo}
                    inputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Icon 
                            className={classes.inputAdornmentIcon}
                            style={{ cursor: "pointer" }}
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? "visibility_off" : "visibility"}
                          </Icon>
                        </InputAdornment>
                      ),
                      type: showPassword ? "text" : "password",
                      autoComplete: "off",
                    }}
                  />
                )}
              </CardBody>
              <CardFooter className={classes.justifyContentCenter}>
              <GridContainer justifyContent="center">
                  {loginSection && (
                    <>
                      <GridItem xs={12} sm={12} md={12}>
                        <Button color="danger" block
                          onClick={() => {
                            if (loginInfo?.emailAddress && loginInfo?.password) {
                              login(loginInfo);
                            } else {
                              setTR(true);
                              setTRMsg("All fields are mandatory.");
                            }
                          }}
                        >
                          Login
                        </Button>
                      </GridItem>
                      <GridItem xs={12} sm={12} md={12}>
                        <Button color="danger" block
                          onClick={() => setLoginSection(false)}
                        >
                          Reset Password
                        </Button>
                      </GridItem>
                    </>
                  )}
                  {otpSection && (
                    <>
                      <GridItem xs={12} sm={12} md={12}>
                        <Button
                          color="danger"
                          // simple
                          size="md"
                          block
                          onClick={() => {
                            if (loginInfo && loginInfo.emailAddress) {
                              validateResetPasswordUpdate(loginInfo);
                              // setOtpSection(false);
                            } else {
                              setTR(true);
                              setTRMsg("All fields are mandatory.");
                            }
                          }}
                        >
                          Continue
                      </Button>
                      </GridItem>
                      <GridItem xs={12} sm={12} md={12}>
                        <Button
                          color="danger"
                          // simple
                          size="md"
                          block
                          onClick={() => {
                            backToOTP();
                            setInterval(() => {
                              setOtpSection(false)
                            }, 1500)
                          }}
                        >
                          Back to reset
                      </Button>
                      </GridItem>
                    </>
                  )}
                  {!loginSection && !otpSection && (
                    <>
                      <GridItem xs={12} sm={12} md={12}>
                        <Button
                          color="danger"
                          // simple
                          size="md"
                          block
                          onClick={() => {
                            if (loginInfo && loginInfo.emailAddress) {
                              resetPassword(loginInfo);
                              // setOtpSection(true);
                            } else {
                              setTR(true);
                              setTRMsg("All fields are mandatory.");
                            }
                          }}
                        >
                          Reset Password
                        </Button>
                      </GridItem>
                      <GridItem xs={12} sm={12} md={12}>
                        <Button
                          color="danger"
                          // simple
                          size="md"
                          block
                          onClick={() => setLoginSection(true)}
                        >
                          Back to login
                        </Button>
                      </GridItem>
                    </>
                  )}
                </GridContainer>
              </CardFooter>
            </Card>
          </form>
          <div className={classes.appVersion}>
            <a><img width={'90px'} src={logo} alt="logo" className={classes.img} />PORTAL</a>
            <div style={{ fontSize: '12px'}}>v{pjson.version}</div>
          </div>
        </GridItem>
        <Snackbar
          place="tr"
          color="danger"
          icon={AddAlert}
          message={trMsg}
          open={tr}
          closeNotification={() => setTR(false)}
          close
        />
        <Loader status={userData.loading} />
      </GridContainer>
    </div>
  );
}
